import { api, LightningElement, wire } from "lwc";

import { EffectAdapter } from "common/effectAdapter";
import { createTrack } from "common/track";

import type { NavItem, NavItemWithState, NavSection } from "../headerNav/types";
export type { NavItem, NavSection };

enum MenuVariant {
  Default = "default",
  Simple = "simple",
}

// A section inside a column in the menu; purely presentational.
interface ColumnSection extends NavSection {
  key: string;
  variant: MenuVariant;
}

type NavColumn = { key: string; sections: ColumnSection[] };

export default class extends LightningElement {
  @api index: number = 0;
  @api item: NavItemWithState = null!;
  @api layout: "desktop" | "mobile" = "desktop";

  private didFocus = false;
  private track = createTrack<"tds-header-nav-item">(this);
  private variant: MenuVariant = MenuVariant.Default;

  // Cached value to avoid recomputing this.columns.
  private _columns: NavColumn[] = [];

  private get anchor() {
    return this.layout === "desktop" && this.index < 3 ? "left" : "right";
  }

  private get columns() {
    return this._columns;
  }

  private set columns(columns) {
    this._columns = columns;
  }

  @wire(EffectAdapter, { item: "$item" })
  private computeColumns() {
    if (!this.item.sections) return;

    let sectionIndex = 0;
    let simpleSectionCount = 0;
    let sectionColumns = this.item.sectionColumns || [
      this.item.sections.length,
    ];

    // Gets the list of sections for a column given its number of sections.
    const getColumnSections = (sectionCount: number) => {
      const endIndex = sectionIndex + sectionCount;
      const result: ColumnSection[] = [];
      const sections = this.item.sections!.slice(sectionIndex, endIndex);

      for (const section of sections) {
        sectionIndex++;
        const isSimpleSection = this.isSimpleSection(section);
        if (isSimpleSection) simpleSectionCount++;
        result.push({
          ...section,
          key: section.label || "",
          variant: isSimpleSection ? MenuVariant.Simple : MenuVariant.Default,
        });
      }

      return result;
    };

    // Distribute nav item's flat list of sections into different columns.
    // Entries are of type [string, number], e.g. ["0", 2].
    this.columns = Object.entries(sectionColumns).map(
      ([columnIndex, sectionCount]) => ({
        key: `col-${columnIndex}`,
        sections: getColumnSections(sectionCount),
      })
    );

    // Finally, compute the variant for the whole menu.
    // A simple menu has only one column with one simple section.
    this.variant =
      this.columns.length === 1 && simpleSectionCount === 1
        ? MenuVariant.Simple
        : MenuVariant.Default;
  }

  private isSimpleSection(section: NavSection) {
    // A simple section only has items with no url or description.
    return !!(
      section.items &&
      section.items.length &&
      section.items.every((i) => !i.iconUrl && !i.description)
    );
  }

  private get isDesktop() {
    return this.layout === "desktop";
  }

  private onKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case "Esc":
      case "Escape":
        this.dispatchEvent(
          new CustomEvent("requestclose", { detail: this.item })
        );
        break;
      default:
    }
    this.didFocus = false;
  }

  private onLinkClick(e: MouseEvent) {
    this.track("item_clicked", {
      label: (e.currentTarget as HTMLElement).dataset.label ?? "",
    });
  }

  private get showPromo() {
    return !!(this.layout === "desktop" && this.item.promo);
  }

  renderedCallback() {
    if (!this.didFocus && this.item.isExpanded) {
      let element = this.template.querySelector("a") as HTMLElement;
      if (!element) return;
      element.focus();
      this.didFocus = true;
    }
  }
}
